import * as yup from "yup";

import { Button, Grid } from "@mui/material";
import { useEffect, useState } from "react";

import AutoComplete from "../../../../components/hookForms/autoComplete";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FileInputButton from "../../../../components/fileInputButton";
import IGenericResult from "../../../../interfaces/IGenericResult";
import IconButton from "@mui/material/IconButton";
import { getBase64 } from "../../../../utils/utils";
import messages from "../../../../utils/messages";
import serviceRevisaoCotaUsina from "../../../../services/serviceRevisaoCotaUsina";
import serviceUsina from "../../../../services/serviceUsina";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IDialogProps {
    open: boolean;
    onSuccess: () => void;
    onClose?: any;
    returnDate: (item: any[]) => void;
}

export default function DialogNew({ open, onClose, onSuccess, returnDate }: IDialogProps) {
    const [selectedFileBase64, setSelectedFileBase64] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [usinaDataOptions, setUsinaDataOptions] = useState<IGenericResult[]>([]);
    const [isDisable, setDisable] = useState(false);

    const validationSchema = yup.object().shape({
        usinaID: yup.string().required(messages.requiredField),
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            usinaID: "",
        },
    });

    const handleFileInput = (e: any) => {
        const extension = e.target.files[0].name.split(".").pop().toLowerCase();

        if (["csv"].includes(extension)) {
            setSelectedFile(e.target.files[0]);

            const size = e.target.files[0].size / 1024 / 1024;

            if (size > 10) {
                toast.error("O tamanho do arquivo selecionado não pode ser maior do que 10MB");
                clearFile();
            }

            getBase64(e.target.files[0]).then((result) => {
                setSelectedFileBase64(result);
            });
        } else {
            toast.error("O arquivo selecionado é inválido. É necessário anexar o arquivo no formato (CSV).");
            clearFile();
        }
    };

    const onSubmit = (values: any) => {
        setDisable(true);

        if (validate(selectedFile)) {
            const newItem = {
                ...values,
                fileBase64: selectedFileBase64.replace("data:text/csv;base64,", ""),
            };

            serviceRevisaoCotaUsina
                .create(newItem)
                .then((result) => {
                    if (result) {
                        if (result.data && result.data.length > 0) {
                            returnDate(result.data);
                        } else {
                            toast.success(messages.createSucess);
                        }
                    }
                    onCancelNew();
                    reset();
                    onSuccess();
                })
                .finally(() => {
                    setDisable(false);
                    onCancelNew();
                    reset();
                });
        } else {
            setDisable(false);
        }
    };

    useEffect(() => {
        setUsinaData();
    }, []);

    const setUsinaData = async () => {
        serviceUsina
            .getUsinas()
            .then((result) => {
                setUsinaDataOptions(result?.data);
            })
            .catch(() => {
                setUsinaDataOptions([]);
            });
    };

    const onCancelNew = () => {
        reset();
        setSelectedFile(null);
        onClose(true);
    };

    const clearFile = () => {
        setSelectedFile(null);
        setSelectedFileBase64("");
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            onClose={(_, reason) => {
                if (reason !== "backdropClick") {
                    onClose();
                }
            }}
            open={open}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    Nova planilha de revisão de cotas
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            onCancelNew();
                        }}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={12}>
                            <AutoComplete
                                name="usinaID"
                                label="Usina"
                                options={usinaDataOptions}
                                control={control}
                                error={errors}
                            />
                        </Grid>

                        <Grid item xs={12} md={12}>
                            <FileInputButton accept="text/csv" onChange={handleFileInput} selectedFile={selectedFile} />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                    <Button
                        onClick={() => {
                            onCancelNew();
                        }}
                        color="warning"
                        variant="contained"
                        style={{ width: "159px", height: "44px" }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={isDisable}
                        style={{ width: "159px", height: "44px" }}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );

    function validate(selectedFile: any): boolean {
        if (!selectedFile) {
            toast.error("É necessário anexar a planilha (CSV).");
            return false;
        }

        return true;
    }
}
